import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { Award, Loader2 } from 'lucide-react';
import { useLanguage } from '../../context/AppContext';

const BadgesModal = ({
  open,
  onOpenChange,
  badges
}) => {
  const { language, t } = useLanguage();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl uppercase tracking-tight flex items-center gap-2">
            <Award className="w-6 h-6 text-primary" />
            {t('modals.badges_title')}
          </DialogTitle>
          <DialogDescription>
            {badges.total_earned || 0} / {badges.total_available || 10} {language === 'fr' ? 'débloqués' : 'unlocked'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-6">
          {/* Earned Badges */}
          {badges.earned?.length > 0 && (
            <div>
              <h3 className="font-heading text-lg uppercase tracking-tight text-primary mb-4">
                {language === 'fr' ? 'Badges Obtenus' : 'Earned Badges'}
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {badges.earned.map((badge, index) => (
                  <div
                    key={badge.id}
                    className="flex items-center gap-3 p-4 rounded-lg"
                    style={{ backgroundColor: `${badge.color}15`, border: `2px solid ${badge.color}` }}
                  >
                    {badge.badge_image ? (
                      <img src={badge.badge_image} alt={badge.name} className="w-10 h-10 object-contain" />
                    ) : (
                      <span className="text-3xl">{badge.icon}</span>
                    )}
                    <div>
                      <p className="font-bold" style={{ color: badge.color }}>{badge.name}</p>
                      <p className="text-xs text-muted-foreground">{badge.description}</p>
                      <p className="text-xs text-muted-foreground mt-1 capitalize">
                        {badge.rarity === 'legendary' && '⭐ '}
                        {badge.rarity === 'epic' && '💜 '}
                        {badge.rarity === 'rare' && '💙 '}
                        {badge.rarity === 'uncommon' && '💚 '}
                        {badge.rarity}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Progress Badges */}
          {badges.progress?.length > 0 && (
            <div>
              <h3 className="font-heading text-lg uppercase tracking-tight text-muted-foreground mb-4">
                {language === 'fr' ? 'En Progression' : 'In Progress'}
              </h3>
              <div className="space-y-3">
                {badges.progress.map((badge, index) => (
                  <div
                    key={badge.id}
                    className="flex items-center gap-4 p-4 rounded-lg bg-secondary/50 border border-border"
                  >
                    <span className="text-2xl opacity-40">{badge.icon}</span>
                    <div className="flex-1">
                      <div className="flex justify-between items-center mb-2">
                        <p className="font-medium text-foreground">{badge.name}</p>
                        <span className="text-sm text-muted-foreground">
                          {badge.current} / {badge.target}
                        </span>
                      </div>
                      <Progress value={badge.progress_percent} className="h-2" />
                      <p className="text-xs text-muted-foreground mt-1">{badge.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {badges.earned?.length === 0 && badges.progress?.length === 0 && (
            <div className="text-center py-12">
              <Award className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-30" />
              <p className="text-muted-foreground">
                {t('modals.badges_empty')}
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BadgesModal;
